import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import useSubscription from '../../hooks/useSubscription';
import PremiumGate from '../../components/PremiumGate';
import CreateCampaignTab from './components/CreateCampaignTab';
import ActiveCampaignsTab from './components/ActiveCampaignsTab';
import AdAnalyticsTab from './components/AdAnalyticsTab';
import TemplatesTab from './components/TemplatesTab';
import SegmentsTab from './components/SegmentsTab';
import CampaignOptimizerTab from './components/CampaignOptimizerTab';

const TABS = [
  { id: 'create', label: 'Create Campaign', icon: 'Plus' },
  { id: 'active', label: 'Active Campaigns', icon: 'Megaphone' },
  { id: 'optimizer', label: 'AI Optimizer', icon: 'Sparkles' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart2' },
  { id: 'templates', label: 'Templates', icon: 'FileText' },
  { id: 'segments', label: 'Segments', icon: 'Users' },
];

const AIMarketingPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { profile, organization } = useAuth();
  const { canAccess, isOrgLoading } = useSubscription();
  const [activeTab, setActiveTab] = useState('create');
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    const { data, error } = await supabase
      ?.from('ad_campaigns')
      ?.select('*')
      ?.eq('org_id', profile?.org_id)
      ?.order('created_at', { ascending: false });
    if (!error) setCampaigns(data || []);
    setLoading(false);
  }, [profile?.org_id]);

  const fetchSegments = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase
      ?.from('segments')
      ?.select('*')
      ?.eq('org_id', profile?.org_id)
      ?.order('created_at', { ascending: false });
    setSegments(data || []);
  }, [profile?.org_id]);

  useEffect(() => {
    fetchCampaigns();
    fetchSegments();
  }, [fetchCampaigns, fetchSegments]);

  const activeCampaignsCount = campaigns?.filter(c => c?.status === 'active')?.length;
  const totalSpend = campaigns?.reduce((s, c) => s + (parseFloat(c?.spend) || 0), 0);
  const totalLeads = campaigns?.reduce((s, c) => s + (c?.leads || 0), 0);
  const totalImpressions = campaigns?.reduce((s, c) => s + (c?.impressions || 0), 0);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Helmet>
        <title>AI Marketing — Wilcart CRM</title>
      </Helmet>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header onMenuToggle={() => setIsSidebarOpen(true)} />
        {isOrgLoading ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !canAccess('aiMarketing') ? (
          <PremiumGate
            featureName="AI Marketing"
            featureIcon="Megaphone"
            description="Create AI-powered campaigns, manage ad analytics, build audience segments, and automate your marketing — all from one place."
            requiredPlan="Full Suite"
            planId="crm_voice_website_marketing"
          />
        ) : (
          <>
            <main className="flex-1 lg:ml-64 pt-16 lg:pt-20 overflow-y-auto min-h-0">
              <div className="p-4 lg:p-6 max-w-screen-2xl mx-auto">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center shadow-sm">
                      <Icon name="Megaphone" size={20} color="white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-foreground">AI Marketing</h1>
                      <p className="text-xs text-violet-600">Facebook · Instagram · Google Ads · Powered by Claude AI</p>
                    </div>
                  </div>
                  {/* Platform connection status */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      organization?.meta_access_token
                        ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-muted text-muted-foreground border-border'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${organization?.meta_access_token ? 'bg-blue-500 animate-pulse' : 'bg-muted-foreground'}`} />
                      Meta Ads
                    </div>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      organization?.google_ads_refresh_token
                        ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-muted text-muted-foreground border-border'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${organization?.google_ads_refresh_token ? 'bg-orange-500 animate-pulse' : 'bg-muted-foreground'}`} />
                      Google Ads
                    </div>
                  </div>
                </div>

                {/* Stats Strip */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Active Campaigns', value: activeCampaignsCount, icon: 'Megaphone', color: 'text-violet-600' },
                    { label: 'Total Impressions', value: totalImpressions?.toLocaleString(), icon: 'Eye', color: 'text-blue-600' },
                    { label: 'Total Leads', value: totalLeads, icon: 'Users', color: 'text-green-600' },
                    { label: 'Total Spend', value: `$${totalSpend?.toFixed(2)}`, icon: 'DollarSign', color: 'text-orange-600' },
                  ]?.map((stat, i) => (
                    <div key={i} className="bg-background border border-border rounded-xl p-4 shadow-sm">
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon name={stat?.icon} size={16} className={stat?.color} />
                        <span className="text-xs text-muted-foreground">{stat?.label}</span>
                      </div>
                      <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                    </div>
                  ))}
                </div>

                {/* Tabs */}
                <div className="flex border-b border-border mb-6 overflow-x-auto scrollbar-none">
                  {TABS?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                        activeTab === tab?.id
                          ? 'text-violet-600 border-b-2 border-violet-600'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                      {tab?.id === 'active' && activeCampaignsCount > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs font-bold">
                          {activeCampaignsCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                {loading && activeTab !== 'templates' && activeTab !== 'segments' ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {activeTab === 'create' && (
                      <CreateCampaignTab
                        orgId={profile?.org_id}
                        organization={organization}
                        onCampaignCreated={() => { fetchCampaigns(); setActiveTab('active'); }}
                      />
                    )}
                    {activeTab === 'active' && (
                      <ActiveCampaignsTab
                        campaigns={campaigns}
                        onRefresh={fetchCampaigns}
                        organization={organization}
                      />
                    )}
                    {activeTab === 'optimizer' && (
                      <CampaignOptimizerTab
                        campaigns={campaigns}
                        onRefresh={fetchCampaigns}
                        organization={organization}
                      />
                    )}
                    {activeTab === 'analytics' && (
                      <AdAnalyticsTab campaigns={campaigns} />
                    )}
                    {activeTab === 'templates' && (
                      <TemplatesTab
                        onUseTemplate={(template) => { setActiveTab('create'); }}
                      />
                    )}
                    {activeTab === 'segments' && (
                      <SegmentsTab
                        segments={segments}
                        orgId={profile?.org_id}
                        onSegmentsUpdated={fetchSegments}
                      />
                    )}
                  </>
                )}
              </div>
            </main>
          </>
        )}
      </div>
    </div>
  );
};

export default AIMarketingPage;
